import React, { useState, useEffect } from "react";
import { 
  ClockIcon, 
  SettingsIcon, 
  MailIcon, 
  KeyIcon, 
  PlusIcon, 
  TrashIcon, 
  XIcon,
  Eye, 
  EyeOff, 
  Edit 
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import { fetchSettings, updateSettings } from "../services/settingsService";

function EnquiryAlertsSettings() {
  // State for General Settings
  const [operationalHours, setOperationalHours] = useState("");

  // State for Auto Renew Listing Bot
  const [spareroomEmail, setSpareroomEmail] = useState("");
  const [spareroomPassword, setSpareroomPassword] = useState("");
  const [autoRenewMin, setAutoRenewMin] = useState(61);
  const [autoRenewMax, setAutoRenewMax] = useState(75);

  // State for Email Automation Bot
  const [emailUsername, setEmailUsername] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [messageTemplate, setMessageTemplate] = useState("");
  const [emailTimingOption, setEmailTimingOption] = useState("every");
  const [emailIntervalHours, setEmailIntervalHours] = useState(1);
  const [emailSpecificTime, setEmailSpecificTime] = useState("");

  // State for Monitored Email Subjects
  const [emailSubjects, setEmailSubjects] = useState(["Spareroom email alert"]);
  const [isAddSubjectModalOpen, setIsAddSubjectModalOpen] = useState(false);
  const [newSubject, setNewSubject] = useState("");

  // State for Validation Errors
  const [errors, setErrors] = useState({});

  // New state variables
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    spareroom: false,
    email: false
  });

  // Update the validation messages
  const validateForm = () => {
    const newErrors = {};

    if (!spareroomEmail) {
      newErrors.spareroomEmail = "Bot needs an email for SpareRoom access";
    }
    if (!spareroomPassword) {
      newErrors.spareroomPassword = "Bot needs a password for SpareRoom access";
    }
    if (!emailUsername) {
      newErrors.emailUsername = "Bot needs an email for automation";
    }
    if (!emailPassword) {
      newErrors.emailPassword = "Bot needs an email password for automation";
    }
    // ... rest of validation

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetchSettings();
        console.log('API Response:', response); // Debug log
        
        if (response.success && response.data) {
          const settings = response.data;
          
          // Set General Settings
          setOperationalHours(settings.operationalHours);
          
          // Set Auto Renew Bot Settings
          if (settings.autoRenewBot) {
            setSpareroomEmail(settings.autoRenewBot.spareroomEmail);
            setSpareroomPassword(settings.autoRenewBot.spareroomPassword);
            if (settings.autoRenewBot.intervalRange) {
              setAutoRenewMin(settings.autoRenewBot.intervalRange.min);
              setAutoRenewMax(settings.autoRenewBot.intervalRange.max);
            }
          }
          
          // Set Email Automation Bot Settings
          if (settings.emailAutomationBot) {
            setEmailUsername(settings.emailAutomationBot.emailUsername);
            setEmailPassword(settings.emailAutomationBot.emailPassword);
            setMessageTemplate(settings.emailAutomationBot.messageTemplate);
          }
          
          // Set Monitored Subjects
          if (Array.isArray(settings.monitoredSubjects)) {
            setEmailSubjects(settings.monitoredSubjects);
          }
          
          setIsEditMode(false);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        toast.error("Failed to load settings");
      }
    };

    loadSettings();
  }, []);
  
  // Update the handleSubmit function to clarify these are bot settings
  const handleSubmit = async () => {
    if (validateForm()) {
      const botSettings = {
        operationalHours,
        emailAutomationBot: {
          emailUsername,
          emailPassword,
          messageTemplate,
          timing: emailTimingOption === "every" 
            ? { type: "every", hours: emailIntervalHours }
            : { type: "specific", time: emailSpecificTime }
        },
        autoRenewBot: {
          spareroomEmail,
          spareroomPassword,
          intervalRange: { 
            min: autoRenewMin, 
            max: autoRenewMax 
          }
        },
        monitoredSubjects: emailSubjects
      };

      try {
        await updateSettings(botSettings);
        toast.success("Bot settings updated successfully");
        setIsEditMode(false);
      } catch (error) {
        console.error('Error updating settings:', error);
        toast.error("Failed to update bot settings: " + (error.response?.data?.message || error.message || "Unknown error"));
      }
    }
  };
  

  const handleAddSubject = () => {
    if (newSubject.trim()) {
      setEmailSubjects([...emailSubjects, newSubject.trim()]);
      setNewSubject("");
      setIsAddSubjectModalOpen(false);
    }
  };

  const handleRemoveSubject = (indexToRemove) => {
    setEmailSubjects(emailSubjects.filter((_, index) => index !== indexToRemove));
  };

  const SettingsView = ({ settings, onEdit }) => {
    return (
      <div className="bg-white shadow-lg rounded-lg p-6 space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Bot Configuration</h2>
          <button
            onClick={onEdit}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Bot Settings
          </button>
        </div>
  
        {/* General Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <ClockIcon className="mr-3 w-6 h-6 text-blue-600" />
            General Settings
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">
              <span className="font-semibold">Operational Hours:</span> {settings.operationalHours}
            </p>
          </div>
        </div>
  
        {/* Auto Renew Bot Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <KeyIcon className="mr-3 w-6 h-6 text-blue-600" />
            Auto Renew Listing Bot
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <p className="text-gray-700">
              <span className="font-semibold">SpareRoom Email:</span> {settings.autoRenewBot.spareroomEmail}
            </p>
            <p className="text-gray-700">
              <span className="font-semibold">Renewal Interval:</span> {settings.autoRenewBot.intervalRange.min} - {settings.autoRenewBot.intervalRange.max} hours
            </p>
          </div>
        </div>
  
        {/* Email Automation Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <MailIcon className="mr-3 w-6 h-6 text-blue-600" />
            Email Automation Bot
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <p className="text-gray-700">
              <span className="font-semibold">Monitoring Email:</span> {settings.emailAutomationBot.emailUsername}
            </p>
            <div className="border-t border-gray-200 my-2 pt-2">
              <p className="text-gray-700 font-semibold">Message Template:</p>
              <p className="text-gray-600 whitespace-pre-wrap mt-1">
                {settings.emailAutomationBot.messageTemplate}
              </p>
            </div>
          </div>
        </div>
  
        {/* Monitored Subjects */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <MailIcon className="mr-3 w-6 h-6 text-blue-600" />
            Monitored Email Subjects
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <ul className="space-y-2">
              {settings.monitoredSubjects.map((subject, index) => (
                <li key={index} className="text-gray-700 flex items-center">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  {subject}
                </li>
              ))}
            </ul>
          </div>
        </div>
  
        <div className="text-sm text-gray-500 border-t pt-4 mt-4">
          <p>These settings are used by automated bots and are securely stored.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex-grow ml-64 p-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
          <SettingsIcon className="mr-4 w-8 h-8 text-blue-600" />
          Enquiry Alerts Settings
        </h2>

        {isEditMode ? (
          // Existing form code
          <div className="bg-white shadow-lg rounded-lg p-6 space-y-10">
            {/* General Settings */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <ClockIcon className="mr-3 w-6 h-6 text-blue-600" />
                General Settings
              </h3>
              <div>
                <label className="block text-gray-600">Operational Hours</label>
                <input
                  type="text"
                  value={operationalHours}
                  onChange={(e) => setOperationalHours(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg ${
                    errors.operationalHours ? "border-red-500" : "border-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter operational hours (e.g., 9:00 AM - 6:00 PM)"
                />
                {errors.operationalHours && <p className="text-red-500 text-sm">{errors.operationalHours}</p>}
              </div>
            </div>
  
            {/* Auto Renew Listing Bot */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <KeyIcon className="mr-3 w-6 h-6 text-blue-600" />
                Auto Renew Listing Bot
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600">SpareRoom Login</label>
                  <input
                    type="text" // Changed from type="email"
                    value={spareroomEmail}
                    onChange={(e) => setSpareroomEmail(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg ${
                      errors.spareroomEmail ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter SpareRoom login email"
                    autoComplete="off" // Prevent browser suggestions
                  />
                </div>
                <div>
                  <label className="block text-gray-600">SpareRoom Access Key</label>
                  <input
                    type="text" // Changed from type="password"
                    value={spareroomPassword}
                    onChange={(e) => setSpareroomPassword(e.target.value)}
                    autoComplete="off"
                    className={`w-full px-4 py-3 border rounded-lg ${
                      errors.spareroomPassword ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter access key for SpareRoom"
                  />
                </div>
                <div className="flex space-x-4">
                  <input
                    type="number"
                    value={autoRenewMin}
                    onChange={(e) => setAutoRenewMin(Number(e.target.value))}
                    min="61"
                    className="w-1/2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Min (e.g., 61)"
                  />
                  <input
                    type="number"
                    value={autoRenewMax}
                    onChange={(e) => setAutoRenewMax(Number(e.target.value))}
                    min={autoRenewMin + 1}
                    className="w-1/2 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Max (e.g., 75)"
                  />
                </div>
                {errors.autoRenew && <p className="text-red-500 text-sm">{errors.autoRenew}</p>}
              </div>
            </div>
  
            {/* Email Automation Bot */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <MailIcon className="mr-3 w-6 h-6 text-blue-600" />
                Email Automation Bot
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600">Monitoring Email</label>
                  <input
                    type="text" // Changed from type="email"
                    value={emailUsername}
                    onChange={(e) => setEmailUsername(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg ${
                      errors.emailUsername ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter email for monitoring"
                    autoComplete="off" // Prevent browser suggestions
                  />
                </div>
                <div>
                  <label className="block text-gray-600">Email Access Key</label>
                  <input
                    type="text" // Changed from type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    autoComplete="off"
                    className={`w-full px-4 py-3 border rounded-lg ${
                      errors.emailPassword ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter access key for email"
                  />
                </div>
                <div>
                  <label className="block text-gray-600">Message Template</label>
                  <textarea
                    value={messageTemplate}
                    onChange={(e) => setMessageTemplate(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg ${
                      errors.messageTemplate ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter your message template"
                    rows="5"
                  ></textarea>
                  {errors.messageTemplate && <p className="text-red-500 text-sm">{errors.messageTemplate}</p>}
                </div>
                <div>
                  <label className="block text-gray-600">Timing Option</label>
                  <select
                    value={emailTimingOption}
                    onChange={(e) => setEmailTimingOption(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="every">Every (Set Interval)</option>
                    <option value="specific">At Specific Time</option>
                  </select>
                </div>
                {emailTimingOption === "every" && (
                  <div>
                    <label className="block text-gray-600">Interval (hours)</label>
                    <input
                      type="number"
                      value={emailIntervalHours}
                      onChange={(e) => setEmailIntervalHours(Number(e.target.value))}
                      min="1"
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Interval in hours"
                    />
                    {errors.emailInterval && <p className="text-red-500 text-sm">{errors.emailInterval}</p>}
                  </div>
                )}
                {emailTimingOption === "specific" && (
                  <div>
                    <label className="block text-gray-600">Specific Time</label>
                    <input
                      type="time"
                      value={emailSpecificTime}
                      onChange={(e) => setEmailSpecificTime(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.emailSpecificTime && <p className="text-red-500 text-sm">{errors.emailSpecificTime}</p>}
                  </div>
                )}
              </div>
            </div>
  
            {/* Monitored Email Subjects */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <MailIcon className="mr-3 w-6 h-6 text-blue-600" />
                Monitored Email Subjects
              </h3>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setIsAddSubjectModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300"
                >
                  <PlusIcon className="mr-2 w-5 h-5" />
                  Add Subject
                </button>
              </div>
              {emailSubjects.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No email subjects added yet
                </p>
              ) : (
                <ul className="space-y-3">
                  {emailSubjects.map((subject, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                    >
                      <span className="text-gray-700">{subject}</span>
                      <button
                        onClick={() => handleRemoveSubject(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-100 p-2 rounded-full transition"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
  
            {/* Submit Button */}
            <div className="text-right">
              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition"
              >
                Submit
              </button>
            </div>
          </div>
        ) : (
          // New view mode
          <SettingsView
            settings={{
              operationalHours: operationalHours || '9:00 AM - 6:00 PM',
              autoRenewBot: {
                spareroomEmail: spareroomEmail || 'Not set',
                spareroomPassword: spareroomPassword || '••••••••',
                intervalRange: { 
                  min: autoRenewMin || 61, 
                  max: autoRenewMax || 75 
                }
              },
              emailAutomationBot: {
                emailUsername: emailUsername || 'Not set',
                emailPassword: emailPassword || '••••••••',
                messageTemplate: messageTemplate || 'Default template not set'
              },
              monitoredSubjects: emailSubjects.length > 0 
                ? emailSubjects 
                : ['Spareroom email alert']
            }}
            onEdit={() => setIsEditMode(true)}
          />
        )}

        {/* ... existing modal code ... */}
        {isAddSubjectModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-96">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Add New Subject</h3>
                <button
                  onClick={() => setIsAddSubjectModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              <input
                type="text"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter email subject"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddSubject();
                }}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsAddSubjectModalOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSubject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EnquiryAlertsSettings;